/**
 * @file useActiveGroup.ts
 * @description Какая группа сейчас «под глазами» — для оглавления панели
 * (phase19.16, SB122; uiux-settings-groups §2).
 *
 * `IntersectionObserver` с корнем — прокручиваемой панелью (не окном):
 * зона наблюдения — верхние 40 % панели под липкой строкой. Активная —
 * первая группа, задевающая зону; в самом конце прокрутки — последняя
 * (короткую нижнюю группу иначе не выбрать). Пока идёт программная
 * прокрутка после нажатия на пилюлю, отслеживание молчит — иначе активная
 * дрожит, пролетая группы.
 */

import { useEffect } from "react";

import type { SettingsGroupsApi } from "./context";
import { findScrollParent } from "./scroll";

/** Доля высоты панели под липкой строкой, где группа считается активной. */
const ZONE = 0.4;

export function useActiveGroup(api: SettingsGroupsApi, enabled: boolean): void {
  const { groups, rootRef, setActive, scrollingRef, stickyHeight } = api;

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === "undefined") return;
    const container = findScrollParent(rootRef.current);

    const compute = () => {
      if (scrollingRef.current) return;
      const box = container?.getBoundingClientRect();
      const top = (box?.top ?? 0) + stickyHeight;
      const height = (container?.clientHeight ?? window.innerHeight) - stickyHeight;
      const zoneBottom = top + height * ZONE;
      const atEnd = container ? container.scrollTop + container.clientHeight >= container.scrollHeight - 2 : false;
      let found: string | null = null;
      for (const g of groups) {
        const rect = g.el?.getBoundingClientRect();
        if (!rect) continue;
        // В конце прокрутки — последняя группа, чей заголовок на экране.
        if (atEnd && rect.top < top + height) found = g.id;
        else if (!atEnd && rect.bottom > top && rect.top < zoneBottom) {
          found = g.id;
          break;
        }
      }
      if (found) setActive(found);
    };

    const observer = new IntersectionObserver(compute, {
      root: container,
      rootMargin: `-${stickyHeight}px 0px -${Math.round((1 - ZONE) * 100)}% 0px`,
      threshold: [0, 1],
    });
    for (const g of groups) if (g.el) observer.observe(g.el);
    // Конец прокрутки зона не ловит (нижняя группа короткая) — проверяем по событию.
    const onScroll = () => {
      if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 2) compute();
    };
    container?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      container?.removeEventListener("scroll", onScroll);
    };
  }, [enabled, groups, rootRef, setActive, scrollingRef, stickyHeight]);
}
